import React, { createContext, useContext, useEffect, useState } from 'react';
import { Background, OrderPackage, Student, StudentImage, Option, ImageType, Package, Extra, Price, BackgroundSet, School } from '../models/_models';
import { Cart } from '../models';
import useDeepCompareEffect from 'use-deep-compare-effect';

export const CustomerContext = createContext({});

export function useCustomer() {
  return useContext(CustomerContext);
}

export const CustomerContextProvider = ({ children }) => {
  const [customer, changeValue] = useState(initialState());
  const [isInit, setIsInit] = useState(false);

  const setCustomer = (newCustomer) => {
    changeValue(newCustomer);

    if (typeof newCustomer !== 'object') return;

    // Spraypaint can deserialize cart payloads with circular refs between
    // OrderPackage <-> StudentImage (StudentImageResource has_many
    // :order_packages, always_include_resource_ids). JSON.stringify will
    // throw on those. The React state is already updated above, so the
    // app stays consistent; we just skip persisting that particular
    // snapshot to localStorage.
    try {
      localStorage.setItem(
        'customer',
        JSON.stringify({
          ...newCustomer,
          currentOrderPackage: undefined,
          apptCart: undefined,
          priorityCart: undefined,
        }),
      )
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('CustomerContext.setCustomer: skipped localStorage persist', err?.message);
    }
  };

  useEffect(() => {
    const tempCustomer = localStorage.getItem('customer')
    if (tempCustomer && JSON.parse(tempCustomer)) {
      const school = JSON.parse(tempCustomer).currentSchool

      if (!school) return

      School
        .find(school.id)
        .then((res) => setCustomer({ ...JSON.parse(tempCustomer), currentSchool: res.data }))
        .catch(() => setCustomer(JSON.parse(tempCustomer)))
    }
  }, []);

  useDeepCompareEffect(() => {
    if (customer && customer.cart) return;

    const newCart = new Cart();

    newCart.save()
      .then(() => setCustomer((prev) => ({ ...prev, cart: newCart })))
      .catch((e) => console.error('Failed to create cart:', e));
  }, [customer || {}]);

  useEffect(() => {
    if (!customer) return;
    if (!customer.email) return;

    const keys = ['priorityCart', 'apptCart', 'cart'];

    keys.forEach((async (key) => {
      if (!customer[key]) return;

      try {
        const cart = await Cart.find(customer[key].id).then((res) => res.data);

        if (!cart) return;

        cart.email = customer.email;
        await cart.save();
        cart.email = customer.email;

        setCustomer((prev) => ({ ...prev, [key]: cart }));
      } catch (e) {
        console.error(`Failed to update ${key} cart:`, e);
      }
    }));
  }, [customer && customer.email]);

  const clearCart = () => {
    const newCart = new Cart({ email: customer?.email });

    newCart.save().then(() => setCustomer((prev) => ({ ...prev, cart: newCart, apptCart: undefined })));
  }

  return (
    <CustomerContext.Provider value={{ customer, setCustomer, clearCart }}>
      {children}
    </CustomerContext.Provider>
  );
};

function initialState() {
  const storedCustomer = localStorage.getItem('customer');

  if (storedCustomer) {
    const parsed = JSON.parse(storedCustomer);

    if (Array.isArray(parsed.cart)) {
      localStorage.removeItem('customer');

      return { cart: null, email: '', currentSchool: null, currentStudent: null, currentOrderPackage: null };
    }

    return rehydrate(parsed);
  }

  const newCustomer = { cart: null, email: '', currentSchool: null, currentStudent: null, currentOrderPackage: null }

  return newCustomer;
}

function rehydrate(customer) {
  // const { currentStudent } = customer;
  // FIXME: passing cart to constructor throws error
  // TODO: this whole process kind of sucks actually. need a better way to store temporary models in the client
  // if (customer.cart) customer.cart = new Cart(customer.cart);
  // if (currentStudent) customer.currentStudent = new Student(currentStudent);

  return customer;
}
